// src/pages/workflow/TemplateListPage.tsx
// Browse and install pipeline templates (marketplace).

import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useWorkflow, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  ErrorAlert,
} from '@react/components';
import {
  getEntityId,
} from '@/features/workflow/logic';
import { Select } from '@/components/Select';

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function VerifiedIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 12 15 16 9" fill="none" />
    </svg>
  );
}

function TemplateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  );
}

const CATEGORY_ALL = 'all';

export const TemplateListPage = observer(() => {
  const store = useWorkflow();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(CATEGORY_ALL);
  const [installingId, setInstallingId] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    store.ListTemplates?.()
      ?.then((res: any) => setTemplates(Array.isArray(res?.items) ? res.items : Array.isArray(store.items) ? store.items : []))
      ?.catch(() => {})
      ?.finally(() => setIsLoading(false));
  }, []);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    for (const t of templates) {
      if (t.category) cats.add(t.category);
    }
    return Array.from(cats).sort();
  }, [templates]);

  const filtered = useMemo(() => {
    let items = templates;
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(
        (tmpl: any) =>
          (tmpl.name || '').toLowerCase().includes(q) ||
          (tmpl.description || '').toLowerCase().includes(q) ||
          (tmpl.tagline || '').toLowerCase().includes(q) ||
          (tmpl.tags || []).some((tag: string) => tag.toLowerCase().includes(q)),
      );
    }
    if (categoryFilter !== CATEGORY_ALL) {
      items = items.filter((tmpl: any) => tmpl.category === categoryFilter);
    }
    return items;
  }, [templates, search, categoryFilter]);

  const handleInstall = async (templateId: string) => {
    setInstallingId(templateId);
    try {
      const result = await store.InstallTemplate?.(templateId, {});
      const pipelineId = getEntityId(result?.pipeline ?? result);
      if (pipelineId) {
        navigate(`/workflows/${pipelineId}`);
      }
    } catch {
      // Error handled by store
    } finally {
      setInstallingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <nav className="text-sm mb-2">
          <Link to="/workflows" className="text-muted-foreground hover:text-foreground">{t('workflow.pipelines')}</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('workflow.templates')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('workflow.templates')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('workflow.templatesDesc')}</p>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <Input
          placeholder={t('workflow.searchTemplates')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1"
        />
        {categories.length > 0 && (
          <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} fullWidth={false}>
            <option value={CATEGORY_ALL}>{t('workflow.allCategories')}</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </Select>
        )}
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Template grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-5">
                <div className="animate-pulse space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-muted rounded-lg" />
                    <div className="flex-1">
                      <div className="h-4 bg-muted rounded w-3/4" />
                      <div className="h-3 bg-muted rounded w-1/2 mt-2" />
                    </div>
                  </div>
                  <div className="h-3 bg-muted rounded w-full" />
                  <div className="h-3 bg-muted rounded w-4/5" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <TemplateIcon className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-1">{t('workflow.noTemplates')}</h3>
            <p className="text-sm text-muted-foreground">{t('workflow.noTemplatesDesc')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((tmpl: any) => {
            const tmplId = getEntityId(tmpl);
            const isInstalling = installingId === tmplId;
            return (
              <Card key={tmplId} hoverable className="h-full">
                <CardContent className="p-5 flex flex-col h-full">
                  {/* Template header */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      {tmpl.icon_url ? (
                        <img src={tmpl.icon_url} alt="" className="w-6 h-6" />
                      ) : (
                        <TemplateIcon className="w-5 h-5 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <h3 className="font-semibold text-foreground truncate">{tmpl.name}</h3>
                        {tmpl.is_verified && (
                          <VerifiedIcon className="h-4 w-4 text-primary flex-shrink-0" />
                        )}
                      </div>
                      {tmpl.tagline && (
                        <p className="text-xs text-muted-foreground">{tmpl.tagline}</p>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  {tmpl.description && (
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-3 flex-1">
                      {tmpl.description}
                    </p>
                  )}

                  {/* Tags */}
                  {tmpl.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {tmpl.tags.slice(0, 4).map((tag: string) => (
                        <span key={tag} className="px-1.5 py-0.5 text-xs rounded bg-muted text-muted-foreground">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-border mt-auto">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <DownloadIcon className="h-3 w-3" />
                        {tmpl.install_count || 0}
                      </span>
                      {tmpl.category && <span>{tmpl.category}</span>}
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleInstall(tmplId)}
                      disabled={isInstalling}
                      isLoading={isInstalling}
                    >
                      {t('workflow.install')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
});

export default TemplateListPage;
